import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchCurrentUser,
  clearCachedUser,
  setIncomingDisplayName,
  usesDevAuthStorage,
} from "../../utils/common";
import { postSessionStart } from "../../utils/sessionActivity";

export const loadCurrentUser = createAsyncThunk(
  "auth/loadCurrentUser",
  async (_, { rejectWithValue }) => {

    // LOCAL DEV — if sessionStorage has auth values, use them directly.
    // Populate via URL (?user_email=…&business_function=…) — see bootstrapAuthFromUrl —
    // or manually in DevTools → Application → sessionStorage.
    // Requires REACT_APP_DEV_AUTH=true; production builds never enter this path.
    if (usesDevAuthStorage()) {
      const devEmail =
        sessionStorage.getItem("auth_user_email") ||
        process.env.REACT_APP_DEV_EMAIL;
      const devRole =
        sessionStorage.getItem("auth_business_function") ||
        process.env.REACT_APP_DEV_ROLE;
      if (devEmail && devRole) {
        const devIncoming =
          sessionStorage.getItem("auth_incoming_email") || devEmail;
        const devName =
          sessionStorage.getItem("auth_incoming_name") || devIncoming;
        return {
          email: devEmail,
          business_function: devRole,
          has_access: true,
          name: devName,
          preferred_username: devIncoming,
          email_verified: true,
        };
      }
    }

    // PRODUCTION — fetch identity from the httpOnly cookie via the backend.
    const user = await fetchCurrentUser(true);
    if (!user) return rejectWithValue("Unauthenticated");
    return user;
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    loading: true,
    error: null,
  },
  reducers: {
    clearAuth(state) {
      state.user = null;
      state.loading = false;
      state.error = null;
      clearCachedUser();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        const user = action.payload;
        if (user?.name) {
          setIncomingDisplayName(user.name);
        }
        if (usesDevAuthStorage()) {
          if (user?.email) {
            sessionStorage.setItem("auth_user_email", user.email);
            sessionStorage.setItem("auth_outgoing_email", user.email);
          }
          if (user?.preferred_username) {
            sessionStorage.setItem("auth_incoming_email", user.preferred_username);
          } else if (user?.email) {
            sessionStorage.setItem("auth_incoming_email", user.email);
          }
          if (user?.business_function) {
            sessionStorage.setItem("auth_business_function", user.business_function);
          }
        }

        // UI session tracking: fire ui_session_start once per browser session
        // so we can identify users who land on the performance tab (default view)
        // and leave without asking a question. Runs only when the user actually
        // has access — no need to log unauthenticated bounces.
        if (user?.has_access) {
          try {
            const tab =
              new URLSearchParams(window.location.search).get("tab") ||
              "performance";
            // Fire-and-forget; never blocks the fulfilled reducer.
            postSessionStart(tab);
          } catch {
            // ignore
          }
        }
      })
      .addCase(loadCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load user";
      });
  },
});

export const { clearAuth } = authSlice.actions;
export default authSlice.reducer;
